import {
  BankOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  EditOutlined,
  EnvironmentOutlined,
  FileImageOutlined,
  IdcardOutlined,
  KeyOutlined,
  MinusCircleOutlined,
  PhoneOutlined,
  TeamOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Skeleton,
  Space,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { errorMessage } from '../../api/client';
import {
  fetchDealerDocumentUrl,
  useDealer,
  useResetDealerPassword,
  useUpdateDealer,
  useUploadDealerDocument,
} from '../../api/hooks';
import { useAuth } from '../../context/AuthContext';
import type { DocumentType } from '../../types';
import { date, dateTime } from '../../utils/format';
import { paymentMethodOptions } from '../../utils/paymentMethods';
import DocumentCard from '../common/DocumentCard';
import PersonAvatar from '../common/PersonAvatar';
import { validateDocument } from '../investor/documentRules';

const MOBILE_PATTERN = /^03\d{2}-\d{7}$/;

/** One labelled value with an icon — the building block of the detail cards. */
function Field({ icon, label, value }: { icon: ReactNode; label: string; value?: string | null }) {
  return (
    <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', padding: '10px 0' }}>
      <span style={{ color: '#8c8c8c', fontSize: 16, marginTop: 2, flexShrink: 0 }}>{icon}</span>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 12, color: '#8c8c8c', lineHeight: 1.4 }}>{label}</div>
        <div style={{ fontSize: 14, color: '#141414', wordBreak: 'break-word' }}>
          {value?.trim() ? value : <span style={{ color: '#bfbfbf' }}>Not provided</span>}
        </div>
      </div>
    </div>
  );
}

/** Small pill showing at a glance whether a document is on file. */
function DocumentBadge({ ok, label }: { ok: boolean; label: string }) {
  return (
    <Tooltip title={ok ? `${label} on file` : `${label} missing`}>
      <Tag
        icon={ok ? <CheckCircleFilled /> : <MinusCircleOutlined />}
        color={ok ? 'success' : 'default'}
        style={{ marginInlineEnd: 6 }}
      >
        {label}
      </Tag>
    </Tooltip>
  );
}

/**
 * A dealer's profile — deliberately laid out identically to the investor ProfileTab: header banner
 * with avatar and document badges, then Personal information / Account & access side by side, then
 * the Documents strip. Both sides of the book should read the same way.
 *
 * Shared between the staff detail page and the dealer's own portal, so every write control is gated
 * on `hasRole('Admin')` — a dealer viewing their own profile sees a read-only version.
 */
export default function DealerProfileTab({ dealerId }: { dealerId: number }) {
  const { data, isLoading } = useDealer(dealerId);
  const { hasRole } = useAuth();
  const isAdmin = hasRole('Admin');
  const [editing, setEditing] = useState(false);
  const [form] = Form.useForm();
  const update = useUpdateDealer(dealerId);
  const resetPassword = useResetDealerPassword(dealerId);
  const upload = useUploadDealerDocument(dealerId);

  if (isLoading || !data) return <Skeleton active avatar paragraph={{ rows: 8 }} />;

  const docs = data.documents;
  const isActive = data.status === 'Active';

  const onFinish = async (values: Record<string, unknown>) => {
    try {
      await update.mutateAsync(values);
      message.success('Dealer updated.');
      setEditing(false);
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  const onResetPassword = () =>
    Modal.confirm({
      title: 'Re-issue login password?',
      content: `${data.fullName} will be signed out everywhere and must use the new password to log in again. The current password cannot be recovered — it is stored only as a hash.`,
      okText: 'Re-issue password',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const result = await resetPassword.mutateAsync();
          Modal.success({
            title: 'New login credentials',
            width: 480,
            content: (
              <div>
                <p>
                  Username: <strong>{result.username}</strong>
                </p>
                <p>
                  New password: <strong>{result.generatedPassword}</strong>
                </p>
                <p style={{ color: '#cf1322' }}>Save this password now — it will not be shown again.</p>
              </div>
            ),
          });
        } catch (err) {
          // Shown as a modal, not a toast: a toast fires behind the confirm dialog's mask and is
          // easy to miss entirely, which leaves the action looking like it silently did nothing.
          Modal.error({ title: 'Could not re-issue the password', content: errorMessage(err) });
        }
      },
    });

  const pick = async (type: DocumentType, file: File) => {
    // Same client-side rules as investor documents — format, size and video duration.
    const problem = await validateDocument(file as never, type);
    if (problem) {
      message.error(problem);
      return;
    }

    try {
      await upload.mutateAsync({ type, file });
      message.success('Document uploaded.');
    } catch (err) {
      message.error(errorMessage(err));
    }
  };

  /**
   * Documents are optional when a dealer is created, so a missing one still needs a way in. Shown
   * only while it is missing — once a file is on record it is write-once, like the investor side.
   */
  const uploader = (type: DocumentType, label: string, present: boolean) =>
    !isAdmin || present ? null : (
      <Upload
        beforeUpload={(file) => {
          void pick(type, file as unknown as File);
          return false;
        }}
        showUploadList={false}
        maxCount={1}
      >
        <Button size="small" icon={<UploadOutlined />} loading={upload.isPending} style={{ marginTop: 8 }}>
          {label}
        </Button>
      </Upload>
    );

  return (
    <>
      {/* ---------- header banner ---------- */}
      <Card
        style={{
          marginBottom: 16,
          background: 'linear-gradient(135deg, #f0f5ff 0%, #e6fffb 100%)',
          border: '1px solid #d6e4ff',
        }}
        styles={{ body: { padding: 20 } }}
      >
        <Row gutter={[20, 16]} align="middle" wrap>
          <Col flex="none">
            <PersonAvatar
              hasPhoto={docs.hasPhoto}
              fullName={data.fullName}
              fetchUrl={() => fetchDealerDocumentUrl(dealerId, 'photo')}
              size={96}
            />
          </Col>

          <Col flex="auto" style={{ minWidth: 220 }}>
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Space wrap align="center">
                <Typography.Title level={4} style={{ margin: 0 }}>
                  {data.fullName}
                </Typography.Title>
                <Tag color={isActive ? 'green' : 'default'} style={{ fontWeight: 500 }}>
                  {data.status}
                </Tag>
              </Space>

              <Typography.Text type="secondary">
                {data.fatherName ? `s/o ${data.fatherName}` : 'Dealer'}
              </Typography.Text>

              <Space size={16} wrap style={{ marginTop: 4, color: '#595959' }}>
                <span>
                  <IdcardOutlined /> <strong>{data.dealerCode}</strong>
                </span>
                {data.mobile && (
                  <span>
                    <PhoneOutlined /> {data.mobile}
                  </span>
                )}
                <span>
                  <CalendarOutlined /> Joined {date(data.createdAt)}
                </span>
              </Space>

              <div style={{ marginTop: 8 }}>
                <DocumentBadge ok={docs.hasPhoto} label="Photo" />
                <DocumentBadge ok={docs.hasCnicImage} label="CNIC" />
                <DocumentBadge ok={docs.hasAgreementVideo} label="Agreement" />
              </div>
            </Space>
          </Col>

          {isAdmin && (
            <Col flex="none">
              <Button
                type="primary"
                icon={<EditOutlined />}
                onClick={() => {
                  form.setFieldsValue(data);
                  setEditing(true);
                }}
              >
                Edit profile
              </Button>
            </Col>
          )}
        </Row>
      </Card>

      {/* ---------- details ---------- */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <UserOutlined />
                Personal information
              </Space>
            }
            size="small"
          >
            <Field icon={<UserOutlined />} label="Full name" value={data.fullName} />
            <Field icon={<TeamOutlined />} label="Father's name" value={data.fatherName} />
            <Field icon={<PhoneOutlined />} label="Mobile" value={data.mobile} />
            <Field icon={<IdcardOutlined />} label="CNIC" value={data.cnic} />
            <Field icon={<EnvironmentOutlined />} label="Address" value={data.address} />
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card
            title={
              <Space>
                <BankOutlined />
                Account &amp; access
              </Space>
            }
            size="small"
            /* Only an admin may mint credentials for someone else's account — and the dealer
               viewing their own profile in the portal must never see this control at all. */
            extra={
              isAdmin ? (
                <Button size="small" icon={<KeyOutlined />} loading={resetPassword.isPending} onClick={onResetPassword}>
                  Re-issue password
                </Button>
              ) : undefined
            }
          >
            <Field icon={<IdcardOutlined />} label="Dealer code" value={data.dealerCode} />
            <Field icon={<BankOutlined />} label="Payment method" value={data.paymentMethod} />
            <Field icon={<BankOutlined />} label="Account / wallet number" value={data.accountNumber} />
            <Field icon={<KeyOutlined />} label="Login username" value={data.username} />
            <Field icon={<CalendarOutlined />} label="Created" value={dateTime(data.createdAt)} />
            <Field icon={<CalendarOutlined />} label="Last updated" value={dateTime(data.updatedAt)} />
            {isAdmin && (
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid #f0f0f0' }}>
                <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', lineHeight: 1.5 }}>
                  Passwords are stored hashed and cannot be looked up. If the dealer has lost theirs, re-issue a new
                  one above.
                </Typography.Text>
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24}>
          <Card
            title={
              <Space>
                <FileImageOutlined />
                Documents
              </Space>
            }
            size="small"
            extra={
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                Captured at account opening{docs.updatedAt ? ` · ${dateTime(docs.updatedAt)}` : ''}
              </Typography.Text>
            }
          >
            {/*
              The profile photo is deliberately absent from this grid — it is already shown as the
              avatar in the header above, and repeating it served no purpose. Its upload button
              still appears here when no photo is on file.
            */}
            <Row gutter={[12, 12]}>
              <Col xs={24} sm={12} lg={7} xl={6}>
                <DocumentCard
                  label="CNIC image"
                  exists={docs.hasCnicImage}
                  fetchUrl={() => fetchDealerDocumentUrl(dealerId, 'cnic-image')}
                />
                {uploader('cnic-image', 'Upload CNIC image', docs.hasCnicImage)}
              </Col>
              <Col xs={24} sm={12} lg={7} xl={6}>
                <DocumentCard
                  label="Agreement video"
                  exists={docs.hasAgreementVideo}
                  isVideo
                  fetchUrl={() => fetchDealerDocumentUrl(dealerId, 'agreement-video')}
                />
                {uploader('agreement-video', 'Upload video', docs.hasAgreementVideo)}
              </Col>
            </Row>

            {uploader('photo', 'Upload profile photo', docs.hasPhoto)}
          </Card>
        </Col>
      </Row>

      {/* ---------- edit ---------- */}
      <Modal
        title="Edit dealer"
        open={editing}
        onCancel={() => setEditing(false)}
        onOk={() => form.submit()}
        confirmLoading={update.isPending}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onFinish} preserve={false}>
          <Form.Item name="fullName" label="Full name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="fatherName" label="Father's name">
            <Input />
          </Form.Item>
          <Form.Item name="mobile" label="Mobile" rules={[{ pattern: MOBILE_PATTERN, message: 'Format: 03XX-XXXXXXX' }]}>
            <Input placeholder="0300-1234567" />
          </Form.Item>
          {/* CNIC identifies the dealer and is fixed once the record is opened, same as investors. */}
          <Form.Item name="cnic" label="CNIC" help="CNIC cannot be changed after the dealer is created.">
            <Input disabled />
          </Form.Item>
          <Form.Item name="address" label="Address">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="paymentMethod" label="Payment method">
            <Select placeholder="Select a method" allowClear options={paymentMethodOptions} />
          </Form.Item>
          <Form.Item name="accountNumber" label="Account / wallet number">
            <Input placeholder="Account title, IBAN or wallet number" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
